import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';

interface WishBubbleProps {
  id: string;
  content: string;
  authorName: string;
  displayType: 'full' | 'anonymous' | 'censored';
  timestamp: string;
  resetTrigger?: number; // NEW: trigger to reset position
}

export default function WishBubble({
  id, content, authorName, displayType, timestamp, resetTrigger, dark, hideAvatar, modern, startAnimation
}: WishBubbleProps & { dark?: boolean; hideAvatar?: boolean; modern?: boolean; startAnimation?: boolean }) {
  const navigate = useNavigate();
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dragThreshold = 5; // pixels to consider as drag vs click
  const startTime = useRef<number>(0);
  const clickTimeThreshold = 200; // milliseconds to consider as click
  const startPosition = useRef({ x: 0, y: 0 }); // NEW
  const wasDragged = useRef(false); // NEW
  const [popping, setPopping] = useState(false);
  // Entrance sequencing: bubble stays invisible until startAnimation fires
  // (tied to the hero bubble intro popping), then does a quick pop flash,
  // then falls in. 'measure' is a one-frame invisible step used to read the
  // bubble's true resting position (via layout effect, before paint) so the
  // fall can start from the hero bubble's exact last on-screen position.
  const [entrancePhase, setEntrancePhase] = useState<'waiting' | 'pop' | 'measure' | 'fall'>('waiting');
  const entranceWrapRef = useRef<HTMLDivElement>(null);
  // Offset (in px) the bubble travels from on entrance: computed so the
  // bubble appears to fall from the hero bubble's last position (dead
  // center of the viewport, since the hero is fixed there) to wherever this
  // bubble actually rests — whatever the current scroll position is.
  const [startOffset, setStartOffset] = useState({ x: 0, y: -160 });

  // Deterministic per-bubble variation (derived from the wish id) so multiple
  // bubbles don't all fall in / float / pop in perfect sync — keeps them alive.
  const hash = useMemo(() => {
    let h = 0;
    for (let i = 0; i < id.length; i++) {
      h = (h * 31 + id.charCodeAt(i)) >>> 0;
    }
    return h;
  }, [id]);
  const entranceDelay = useMemo(() => (hash % 5) * 90, [hash]);
  const floatDuration = useMemo(() => 5 + ((hash % 40) / 10), [hash]);
  // Negative delay starts each bubble mid-loop so they don't bob in unison.
  const floatDelay = useMemo(() => -((hash % 50) / 10), [hash]);
  // How far each bubble scatters sideways while bouncing in on entrance,
  // settling back to 0 (its assigned position) by the end of the animation.
  const scatterX = useMemo(() => (hash % 37) - 18, [hash]);

  // Burst particles used for the "pop" effect when a bubble is tapped.
  const shards = useMemo(() => {
    const count = isMobile ? 6 : 9;
    const colors = ['#b4befe', '#a6e3a1', '#f9e2af', '#fab387', '#f38ba8', '#89b4fa'];
    return Array.from({ length: count }, (_, i) => {
      const angle = (360 / count) * i + (hash % 30) - 15;
      const rad = (angle * Math.PI) / 180;
      const distance = (isMobile ? 44 : 60) + ((hash + i * 17) % 40);
      return {
        tx: Math.cos(rad) * distance,
        ty: Math.sin(rad) * distance,
        color: colors[(hash + i) % colors.length],
        size: 6 + ((hash + i * 5) % 6),
        delay: (i % 3) * 15,
      };
    });
  }, [hash, isMobile]);

  // Smaller, tighter burst used for the pre-fall "pop" flash (echoes the
  // hero bubble's materialize-then-burst feel, but subtler per bubble).
  const prepopShards = useMemo(() => {
    const count = 5;
    const colors = ['#b4befe', '#a6e3a1', '#f9e2af', '#89b4fa'];
    return Array.from({ length: count }, (_, i) => {
      const angle = (360 / count) * i + (hash % 24);
      const rad = (angle * Math.PI) / 180;
      const distance = 14 + ((hash + i * 11) % 14);
      return {
        tx: Math.cos(rad) * distance,
        ty: Math.sin(rad) * distance,
        color: colors[(hash + i) % colors.length],
        size: 4 + ((hash + i * 3) % 4),
      };
    });
  }, [hash]);

  const formatCensoredName = (name: string) => {
    return name.split(' ').map(word => `${word[0]}.`).join(' ');
  };

  const displayName = displayType === 'anonymous'
    ? 'Anonymous'
    : displayType === 'censored'
      ? formatCensoredName(authorName)
      : authorName;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // NEW: Reset bubble position when resetTrigger changes.
  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [resetTrigger]);

  // Kick off the pop-flash -> fall-in sequence once startAnimation fires,
  // staggered per bubble via entranceDelay. Guarded by a ref so a later
  // re-render (e.g. an unrelated prop change) doesn't reschedule duplicate
  // timers. Crucially, the cleanup resets the ref whenever ITS OWN timers
  // are cleared before firing \u2014 this makes the effect self-healing under
  // React 18 StrictMode, which intentionally mounts \u2192 cleans up \u2192 remounts
  // every effect once in development. Without the reset, if a bubble's wish
  // data happened to load slowly enough that startAnimation was already
  // `true` on first mount, the dev double-invoke would schedule timers,
  // cancel them in the simulated cleanup, and then the ref guard would
  // block the real remount from ever rescheduling them \u2014 causing the
  // bubble to silently never fall in (the intermittent bug reported).
  const hasStartedEntrance = useRef(false);
  useEffect(() => {
    if (!startAnimation || hasStartedEntrance.current) return;
    hasStartedEntrance.current = true;
    const popTimer = setTimeout(() => setEntrancePhase('pop'), entranceDelay);
    const measureTimer = setTimeout(() => setEntrancePhase('measure'), entranceDelay + 260);
    return () => {
      clearTimeout(popTimer);
      clearTimeout(measureTimer);
      hasStartedEntrance.current = false;
    };
  }, [startAnimation, entranceDelay]);

  // Fires synchronously before the browser paints, so the bubble never
  // visibly flashes at rest before jumping to its animated start position.
  // Reads where the bubble actually sits (entranceWrapRef), compares that to
  // the hero bubble's last position (viewport center), and uses the delta as
  // the entrance animation's starting offset (see the viewport-relative
  // clamp below for why it's bounded).
  useLayoutEffect(() => {
    if (entrancePhase !== 'measure') return;
    const el = entranceWrapRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const bubbleCenterX = rect.left + rect.width / 2;
      const bubbleCenterY = rect.top + rect.height / 2;
      const heroCenterX = window.innerWidth / 2;
      const heroCenterY = window.innerHeight / 2;
      const rawX = heroCenterX - bubbleCenterX;
      const rawY = heroCenterY - bubbleCenterY;
      // Bound the fall distance to the current viewport size rather than a
      // fixed pixel constant: the hero bubble is always on-screen when it
      // pops, so the true distance to a bubble's resting slot is naturally
      // bounded by ~1 viewport height/width. Scaling with viewport size lets
      // the bubble genuinely fall from the hero's real position (e.g. up
      // near the menu cards when scrolled to the top on mobile) while still
      // guarding against runaway transform distances on very long pages.
      const maxX = window.innerWidth * 0.45;
      const maxFallY = window.innerHeight * 0.95;
      const clampedX = Math.max(-maxX, Math.min(maxX, rawX));
      // Always fall from above (negative Y) by a natural amount, following
      // the hero's real direction/distance up to the viewport-height bound.
      const clampedY = Math.max(-maxFallY, Math.min(-70, rawY < 0 ? rawY : -70));
      setStartOffset({ x: clampedX, y: clampedY });
    }
    setEntrancePhase('fall');
  }, [entrancePhase]);

  const handleClick = () => {
    if (!isDragging && !popping) {
      setPopping(true);
    }
  };

  // Navigate to the wish page only after the pop animation has played out,
  // so the click -> burst -> navigate sequence reads as one causal action.
  useEffect(() => {
    if (!popping) return;
    const timer = setTimeout(() => {
      navigate(`/wish/${btoa(id)}`);
    }, 340);
    return () => clearTimeout(timer);
  }, [popping, id, navigate]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (popping) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startTime.current = Date.now();
    const point = { x: e.clientX, y: e.clientY };
    startPosition.current = point; // record initial pointer position
    setDragStart(point);
    wasDragged.current = false; // reset flag
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const currentPoint = { x: e.clientX, y: e.clientY };
    const dx = currentPoint.x - dragStart.x;
    const dy = currentPoint.y - dragStart.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      wasDragged.current = true;
    }
    
    // Apply damping if moving out-of-bounds
    let dampeningX = dx;
    let dampeningY = dy;
    if (bubbleRef.current) {
      const rect = bubbleRef.current.getBoundingClientRect();
      if (rect.left + dx < 0 || rect.right + dx > window.innerWidth) {
        dampeningX = dx * 0.3; // Reduce horizontal movement
      }
      if (rect.top + dy < 0 || rect.bottom + dy > window.innerHeight) {
        dampeningY = dy * 0.3; // Reduce vertical movement
      }
    }
    const newPos = { x: position.x + dampeningX, y: position.y + dampeningY };
    setPosition(newPos);
    setDragStart(currentPoint);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsDragging(false);
    const dragDuration = Date.now() - startTime.current;
    const dx = e.clientX - startPosition.current.x;
    const dy = e.clientY - startPosition.current.y;
    const totalDistance = Math.sqrt(dx * dx + dy * dy);
    // Only treat as a tap if no significant drag occurred
    if (!wasDragged.current && dragDuration < clickTimeThreshold && totalDistance < dragThreshold) {
      handleClick();
    }
    // NEW: Bounce off if bubble is out of bounds
    handleBoundaryBounce();
  };

  // NEW: Bounce helper function
  const handleBoundaryBounce = () => {
    const bubble = bubbleRef.current;
    if (bubble) {
      const rect = bubble.getBoundingClientRect();
      let newX = position.x;
      let newY = position.y;
      if (rect.left < 0) {
        newX -= rect.left;
      }
      if (rect.right > window.innerWidth) {
        newX -= rect.right - window.innerWidth;
      }
      if (rect.top < 0) {
        newY -= rect.top;
      }
      if (rect.bottom > window.innerHeight) {
        newY -= rect.bottom - window.innerHeight;
      }
      // Apply bounce with a smooth transition by updating state
      setPosition({ x: newX, y: newY });
    }
  };

  // NEW: Add resize listener to re-check boundaries on responsive changes
  useEffect(() => {
    const handleResizeEvent = () => {
      handleBoundaryBounce();
    };
    window.addEventListener('resize', handleResizeEvent);
    return () => window.removeEventListener('resize', handleResizeEvent);
  }, [position]);

  // Pilih warna bubble dan font sesuai theme
  const bubbleGradient = dark
    ? "from-[#b4befe]/60 to-[#a6e3a1]/60"
    : "from-[#f2cdcd]/70 to-[#a6e3a1]/60";
  const textColor = dark ? "text-white" : "text-[#232634]";
  const subTextColor = dark ? "text-gray-300" : "text-[#6c6f85]";
  const borderColor = dark ? "border-white/30" : "border-[#e0bfcf]";

  const transform = `translate(${position.x}px, ${position.y}px) scale(${isDragging ? 1.1 : 1})`;

  return (
    <div className="w-full mx-auto px-4 flex justify-center items-center">
      {/* Pop flash: brief materialize burst that plays before the bubble
          falls in, echoing the hero bubble's appear-then-pop feel. */}
      {entrancePhase === 'pop' && (
        <div className="bubble-prepop-wrap" aria-hidden="true">
          <div className="bubble-prepop" />
          {prepopShards.map((shard, i) => (
            <span
              key={i}
              className="bubble-prepop-shard"
              style={{
                ['--tx' as any]: `${shard.tx}px`,
                ['--ty' as any]: `${shard.ty}px`,
                ['--size' as any]: `${shard.size}px`,
                background: shard.color,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}
      {/* Entrance: falls in with a double bounce and sideways scatter once,
          staggered per bubble, starting from the hero bubble's last position.
          Rendered invisibly during 'measure' (one frame, pre-paint) purely to
          read layout position; the actual animation plays during 'fall'. */}
      {(entrancePhase === 'measure' || entrancePhase === 'fall') && (
      <div
        ref={entranceWrapRef}
        className={entrancePhase === 'fall' ? 'bubble-entrance' : ''}
        style={
          entrancePhase === 'fall'
            ? ({
                ['--scatter-x' as any]: `${scatterX}px`,
                ['--start-x' as any]: `${startOffset.x}px`,
                ['--start-y' as any]: `${startOffset.y}px`,
              } as React.CSSProperties)
            : { opacity: 0 }
        }
      >
        {/* Idle: gentle continuous drift/bob so the bubble feels alive. */}
        <div
          className="bubble-idle-float"
          style={{
            ['--float-duration' as any]: `${floatDuration}s`,
            ['--float-delay' as any]: `${floatDelay}s`,
          } as React.CSSProperties}
        >
          <div className="relative">
            <div
              ref={bubbleRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onClick={(e) => {
                if (wasDragged.current) {
                  e.preventDefault();
                  e.stopPropagation();
                } else {
                  handleClick();
                }
              }}
              className={`group relative bg-gradient-to-br ${bubbleGradient} rounded-full p-4 md:p-6 backdrop-blur-lg ${borderColor} shadow-bubble
                ${isDragging ? 'cursor-grabbing shadow-[0_0_30px_rgba(59,130,246,0.5)]' : 'cursor-grab hover:border-blue-500/50'}
                transition-all duration-300 overflow-hidden flex flex-col items-center w-32 h-32 md:w-44 md:h-44 justify-center select-none touch-none ${modern ? 'modern-bubble' : ''} ${popping ? 'bubble-pop-main pointer-events-none' : ''}`}
              style={{
                transform,
                zIndex: isDragging ? 50 : 'auto',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15), 0 4px 24px 0 rgba(80, 150, 255, 0.15)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-full" />
              <div className="relative text-center">
                {/* Hapus avatar/icon, hanya nama dan wish */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  {!hideAvatar && (
                    <div className={`${dark ? 'bg-gray-800' : 'bg-white/80'} p-1.5 rounded-full ${textColor}`}>
                      <User className={`group-hover:text-blue-400 transition-colors ${textColor}`} size={16} />
                    </div>
                  )}
                  <span className={`font-medium text-lg ${textColor}`}>{displayName}</span>
                </div>
                <p className={`text-base md:text-lg line-clamp-2 ${subTextColor}`}>
                  {content}
                </p>
              </div>
            </div>
            {/* Burst particles: only rendered while popping, sit outside the
                bubble's own overflow-hidden circle so they can fly outward. */}
            {popping && (
              <div className="absolute inset-0 pointer-events-none">
                {shards.map((shard, i) => (
                  <span
                    key={i}
                    className="bubble-shard"
                    style={{
                      ['--tx' as any]: `${shard.tx}px`,
                      ['--ty' as any]: `${shard.ty}px`,
                      ['--size' as any]: `${shard.size}px`,
                      background: shard.color,
                      animationDelay: `${shard.delay}ms`,
                    } as React.CSSProperties}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}